import React, { useEffect, useState } from "react";
import {
    signInWithEmailAndPassword,
    setPersistence,
    browserLocalPersistence,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    sendEmailVerification,
} from "firebase/auth";
import { auth } from "../firebase";
import { NavLink, useNavigate } from "react-router-dom";
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Link,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Stack
} from "@mui/material";
import "./auth.css";
import { StringsService } from "../services/strings/strings";
import GoogleIcon from "@mui/icons-material/Google"; // For Google Button
import AppleIcon from "@mui/icons-material/Apple";   // For Apple Button
import AsyncString from "../shared/asyncString";
import { useSignedInUserState } from "../hooks/useAuthState";
import { ProfileType } from "../models/profileType";
import { HAS_VISITED } from "../listingSearchPage/dialog/mapDialog";
import { isAlphanumeric } from "../services/functions/regexHelpers";
import { StaffMappingService } from "../services/profile/staffMapping";
import { CodedError } from "../errors/codedError";

export const LoginPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { profile } = useSignedInUserState();

    const onLogin = async (e: React.FormEvent) => {
        localStorage.setItem(HAS_VISITED, "false");
        e.preventDefault();
        if (loading) {
            return;
        }
        setLoading(true); // Show loading spinner
        setError(null); // Clear previous errors

        try {
            let signInEmail = email;
            // check if this is a username
            if (isAlphanumeric(email)) {
                const staffMapping = await StaffMappingService.getStaffMapping(email)
                if (!staffMapping) {
                    throw new CodedError("StaffLoginFailure", "auth/user-not-found")
                }
                signInEmail = staffMapping.emailAlias
            }

            await setPersistence(auth, browserLocalPersistence); // Persist login state
            const userCredential = await signInWithEmailAndPassword(auth, signInEmail, password);
            const user = userCredential.user;
            console.log(user);
            if (isAlphanumeric(email)) {
                // staff are the only users with usernames and they go straight to the dashboard now
                navigate("/dashboard")
            } else {
                if (!user.emailVerified) {
                    // Optionally re-send the email
                    await sendEmailVerification(user, { url: "https://ecoeats.co.za/verify-success" });
                    navigate("/verify-success")
                }
            }
        } catch (error: any) {
            // Handle specific Firebase error codes
            console.log(error)
            if (error.code === "auth/wrong-password") {
                setError(await StringsService.getStringById("error-wrong-password"));
            } else if (error.code === "auth/user-not-found") {
                setError(await StringsService.getStringById("error-user-not-found"));
            } else if (error.code === "auth/invalid-credential") {
                setError("Username Or Password Is Invalid")
            } else if (error.code === "auth/network-request-failed") {
                setError(await StringsService.getStringById("error-network"));
            } else {
                setError(await StringsService.getStringById("error-unknown"));
            }
            console.error(error.code, error.message);
        } finally {
            setLoading(false); // Hide loading spinner
        }
    };

    const signInWithGoogle = async () => {
        setLoading(true);
        setError(null);

        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            console.log(user)
        } catch (error: any) {
            setError(await StringsService.getStringById("error-google-signin"));
            console.error(error.code, error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (profile) {
            handleNavigation(profile);
        }
    }, [profile]);

    const handleNavigation = (userProfile: ProfileType) => {
        if (userProfile?.profileType === "owner") {
            navigate("/dashboard");
        } else if (userProfile?.profileType === "staff") {
            navigate("/dashboard");
        } else if (userProfile?.profileType === "user") {
            navigate("/search");
        } else if (userProfile?.profileType === "admin") {
            navigate("/adminPortal");
        } else {
            navigate("/search");
        }
    }

    const signInWithApple = async () => {
        setLoading(true);
        setError(null);

        try {
            const provider = new OAuthProvider("apple.com");
            const result = await signInWithPopup(auth, provider);

            const user = result.user;
            console.log(user)
        } catch (error: any) {
            setError(await StringsService.getStringById("error-apple-signin"));
            console.error(error.code, error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='main-container'>
            <Container
                maxWidth="xs"
                sx={{
                    display: "flex",
                    paddingLeft: 8,
                    paddingRight: 8,
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                }}
            >
                <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
                        Welcome to Eco Eats
                    </Typography>
                </Box>
                <Box
                    component="form"
                    onSubmit={onLogin}
                    noValidate
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        width: "100%",
                        background: "#fff",
                        borderRadius: 2,
                        padding: 3,
                        boxShadow: 3,
                    }}
                >
                    <TextField
                        id="email-address"
                        label={<AsyncString stringId="email-address" />}
                        variant="outlined"
                        fullWidth
                        required
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <Box sx={{ width: '100%' }}>
                        <Stack
                            alignItems="end"
                            justifyContent="space-between"
                        >
                            <TextField
                                id="password"
                                label={<AsyncString stringId="password" />}
                                variant="outlined"
                                fullWidth
                                required
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <Link
                                href="/forgotPassword"
                                underline="hover"
                                variant="body2"
                                color="primary"
                            >
                                <AsyncString stringId="forgot-password" />
                            </Link>
                        </Stack>
                    </Box>
                    <Box sx={{ width: '100%', justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    sx={{
                                        color: "#757575",
                                        "&.Mui-checked": { color: "#162C1F" },
                                        "&.MuiCheckbox-root:hover": { backgroundColor: "#E6F0E8" },
                                    }}
                                />
                            }
                            label={<AsyncString stringId="keep-me-signed-in" />}
                        />
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{ background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                    >
                        {loading ?
                            <CircularProgress size={24} color="inherit" /> :
                            <AsyncString stringId="sign-in" />}
                    </Button>
                </Box>


                <Typography variant="subtitle1" marginTop={3} textAlign="center">

                    <AsyncString stringId="or-sign-in-with" />
                </Typography>
                <Stack direction="column" spacing={2} marginTop={2} width="100%">
                    <Button
                        variant="outlined"
                        startIcon={<GoogleIcon style={{ color: "#162C1F" }} />}
                        onClick={signInWithGoogle}
                        fullWidth
                        sx={{ margin: "0 8px", border: "1px solid #162C1F", borderRadius: "24px" }}
                    >
                        <AsyncString
                            stringId="signin-with-google"
                            style={{ color: "#162C1F" }}
                        />
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<AppleIcon style={{ color: "#162C1F" }} />}
                        onClick={signInWithApple}
                        fullWidth
                        sx={{ margin: "0 8px", border: "1px solid #162C1F", borderRadius: "24px" }}
                    >
                        <AsyncString
                            stringId="signin-with-apple"
                            style={{ color: "#162C1F" }}
                        />
                    </Button>
                </Stack>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <AsyncString stringId="no-have-account-account" />
                        <NavLink to="/signup" style={{ color: "#1976d2", textDecoration: "none" }}>
                            <AsyncString stringId="create-account" />
                        </NavLink>
                    </Box>
                </Typography>
                {/* Error Modal */}
                <Dialog open={!!error} onClose={() => setError(null)}>
                    <DialogTitle>
                        <AsyncString stringId="error" />
                    </DialogTitle>
                    <DialogContent>
                        <Typography>{error}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setError(null)} color="primary">
                            <AsyncString stringId="close" />
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </div>
    );
};
